/** @jsxImportSource @emotion/react */
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox } from '@mui/material';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import CustomGrid from 'components/grids/CustomGrid';
import { CommonUtil } from 'utils/CommonUtil';
import { hasRole } from 'utils/SessionUtil';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  StatusDot,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { SetupSchedule, SetupScheduleCondition } from 'models/setup/SetupSchedule';
import { useMessageBar } from 'hooks/useMessageBar';
import {
  checkAvailableRequestEquipment,
  findExcpByElmPrjId,
  findScheduleStatus,
} from 'apis/setup/SetupSchedule';
import { st } from 'components/inputs/CustomInput';
import { GridProgressCellTemplate } from 'components/grids/GridProgressCellRenderer';
import SetupEquipmentScheduleChartPopup from '../popup/SetupEquipmentScheduleChartPopup';
import useSessionStore from 'stores/useSessionStore';
import { getPlanProgressRate, getProgressRateTitle } from 'utils/SetupUtil';
import { checkAvailableProjectManager } from 'apis/setup/SetupProject';
import GridSettingButtons from 'components/grids/GridSettingButtons';
import { findProjectCommonStep } from 'apis/setup/SetupStep';

/**
 * @desc 설비셋업관리 > 프로젝트 셋업관리 > 프로젝트 일정 등록/조회 > [일정현황] 탭 내용
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.24   jshwang12       신규 생성
 * 2025.12.17   jshwang12       해당 프로젝트 FA담당자(본사/법인)도 일정수정 가능
 * 2026.01.19   jshwang12       실적완료요청/승인 버튼 위치 이동 (-> 일정현황상세 탭)
 * 2026.05.21   jihwan89        그리드 핀 고정 설비명까지 추가
 * 2026.05.26   jshwang12       지연 상태 DLY_WRN 추가 (지연(30▼)), 진행상태 label명 변경(작업 시작전 -> 시작전, 실적승인완료 -> 완료, 지연 -> 지연(30▲))
 * 2026.07.01   jshwang12       설비단계자동설정 기능 적용 (공통코드에서 테이블로 변경)
 */
interface Props {
  onScheduleChange: (
    elmPrjId: string,
    excpId: string,
    checkedItems: any[],
    aprReqId?: string
  ) => void;
}

const ScheduleStatusContent = ({ onScheduleChange, ...props }: Props, ref) => {
  const gridRef = useRef<any>();
  const gridUiRef = useRef<any>();
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { ensolYn } = useSessionStore();
  const [condition, setCondition] = useState<SetupScheduleCondition>();
  const [rowData, setRowData] = useState<any[]>([]);
  const [checkedItems, setCheckedItems] = useState<any[]>([]);
  const [code, setCode] = useState<any>();
  const [prjExcpData, setPrjExcpData] = useState<SetupSchedule>();
  const [isOpenScheduleChart, setOpenScheduleChart] = useState<boolean>(false);
  const [scheduleChartCondition, setScheduleChartCondition] = useState<any>();
  const [hasPrjMgrAuth, setHasPrjMgrAuth] = useState<boolean>(false);
  const [isChangeColumn, setIsChangeColumn] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    search: (params) => {
      const p = {
        ...params,
        delayYn: condition?.delayYn || 'N',
      };
      setCondition(p);
      handleSearch(p);
    },
    validate: () => {
      return validate();
    },
  }));

  useEffect(() => {
    if (condition?.elmPrjId) {
      findProjectCommonStep({ elmPrjId: condition.elmPrjId }).then((result) => {
        setCode((prev) => ({
          ...prev,
          prjSpCd: result,
        }));
      });
      if (hasRole('FA_MANAGER')) {
        setHasPrjMgrAuth(true);
      } else {
        checkAvailableProjectManager(condition?.elmPrjId, 'ALL').then((res) =>
          setHasPrjMgrAuth(res)
        );
      }
    } else {
      setHasPrjMgrAuth(false);
    }
  }, [condition?.elmPrjId]);

  useEffect(() => {
    if (condition?.elmPrjId && condition?.excpId) {
      findExcpByElmPrjId(condition.elmPrjId, condition.excpId).then((result) => {
        setPrjExcpData((result || []).length > 0 ? result[0] : undefined);
      });
    } else {
      setPrjExcpData(undefined);
    }
  }, [condition?.elmPrjId, condition?.excpId]);

  const filterRowData = useMemo(() => {
    // 임시
    if (condition?.delayYn === 'Y') {
      return (rowData || []).filter(
        (o) =>
          Object.keys(o).filter(
            (key) => key.endsWith('PRJ_SP_STAT') && ['DLY', 'DLY_WRN'].includes(o[key] || '')
          ).length
      );
    }
    return rowData;
  }, [rowData, condition?.delayYn]);

  const validate = async () => {
    if (!condition?.elmPrjId) {
      openMessageBar({
        type: 'warning',
        content: t(
          'setup.label.대상 프로젝트가 조회 되지 않았습니다.',
          '프로젝트가 조회 되지 않았습니다.'
        ),
      });
      return false;
    }
    if (!condition?.excpId) {
      openMessageBar({
        type: 'warning',
        content: t('setup.label.협력업체를 선택해 주세요.', '협력업체를 선택해 주세요.'),
      });
      return false;
    }
    if (!prjExcpData) {
      openMessageBar({
        type: 'warning',
        content: t('setup.label.협력업체 정보가 없습니다.', '협력업체 정보가 없습니다.'),
      });
      return false;
    }
    if (!(checkedItems || []).length) {
      openMessageBar({
        type: 'warning',
        content: t('setup.label.요청할 설비를 선택해 주세요.', '요청할 설비를 선택해 주세요.'),
      });
      return false;
    }
    const res = await checkAvailableRequestEquipment(
      condition?.elmPrjId,
      condition?.excpId,
      checkedItems.map((o) => o['TGT_EQP_ID'])
    );
    if (res?.successOrNot !== 'Y' || (res?.data || []).length > 0) {
      openMessageBar({
        type: 'warning',
        content:
          res?.successOrNot !== 'Y'
            ? t(
                'setup.label.결재 진행중인 설비 확인 중 오류가 발생했습니다.',
                '결재 진행중인 설비 확인 중 오류가 발생했습니다.'
              )
            : t(
                'setup.label.이미 결재 진행중인 설비가 포함되어 있습니다.',
                '이미 결재 진행중인 설비가 포함되어 있습니다.'
              ),
      });
      return false;
    }
    return true;
  };

  const handleSearch = async (params) => {
    if (params?.elmPrjId) {
      const result = await findScheduleStatus(params?.elmPrjId, params?.excpId);
      setRowData(result);
    } else {
      setRowData([]);
    }
  };

  const layoutDefinition = useMemo(() => {
    const stateCodes = {
      PROG: 'ing',
      DLY_WRN: 'warning', // [26.05.26] 지연 상태 DLY_WRN 추가
      DLY: 'delay',
      CPLT: 'done',
      PLAN: 'none',
    };

    const getProgRtTag = (params) => {
      const statKey = params?.col?.binding?.replace('_PROG_RT', '_PRJ_SP_STAT');
      const crsKey = params?.col?.binding?.replace('_PROG_RT', '_PRJ_SP_CRS');
      const planStDtKey = params?.col?.binding?.replace('_PROG_RT', '_PLAN_ST_DT');
      const planEndDtKey = params?.col?.binding?.replace('_PROG_RT', '_PLAN_END_DT');
      if (!statKey || !crsKey) return '';
      const prjSpStat = params.item[statKey];
      if (!prjSpStat) return '';
      let crs = '';
      if ('DLY' === prjSpStat || 'DLY_WRN' === prjSpStat) {
        crs = params.item[crsKey] || '';
      }
      const state = stateCodes[prjSpStat] || 'none';
      let title = '';
      if (planStDtKey && planEndDtKey) {
        title = `${t('setup.label.계획', '계획')}: ${getPlanProgressRate(
          params.item[planStDtKey],
          params.item[planEndDtKey]
        )}<br/>${t('setup.label.실적', '실적')}: ${getProgressRateTitle(
          params.value || 0,
          Number(crs || 0)
        )}`;
      }
      return GridProgressCellTemplate(state, params.value || 0, Number(crs || 0), title);
    };

    const prjSpLayout = (code?.prjSpCd || []).reduce(
      (acc, cur, idx) => [
        ...acc,
        {
          header: cur.prjSpNm,
          columns: [
            {
              header: String(t('setup.label.계획 시작일', '계획 시작일')),
              binding: `'${cur.prjSpCd}'_PLAN_ST_DT`,
              minWidth: 60,
              width: 87,
              align: 'center',
            },
            {
              header: String(t('setup.label.계획 종료일', '계획 종료일')),
              binding: `'${cur.prjSpCd}'_PLAN_END_DT`,
              minWidth: 60,
              width: 87,
              align: 'center',
            },
            {
              header: String(t('setup.label.현황', '현황')),
              binding: `'${cur.prjSpCd}'_PROG_RT`,
              minWidth: 100,
              width: 150,
              align: 'left',
              cssClass: 'WijmoIconButton',
              cellTemplate: getProgRtTag,
            },
          ],
        },
      ],
      []
    );

    return [
      {
        header: String(t('setup.label.공정', '공정')),
        binding: 'PRDN_PROC_CD_NM',
        minWidth: 70,
        width: 110,
        align: 'center',
      },
      {
        header: String(t('com.label.설비군', '설비군')),
        binding: 'EQCL_ID',
        minWidth: 70,
        width: 80,
        align: 'center',
      },
      {
        header: String(t('setup.label.양/음극', '양/음극')),
        binding: 'ELTR_TP_CD_NM',
        minWidth: 70,
        width: 90,
        align: 'center',
      },
      {
        header: String(t('setup.label.호기', '호기')),
        binding: 'EQP_LN_ID',
        minWidth: 80,
        width: 80,
        align: 'center',
      },
      {
        header: String(t('setup.label.M-CODE', 'M-CODE')),
        binding: 'EQP_ID',
        minWidth: 80,
        width: 110,
        align: 'center',
        cellTemplate: CellMaker.makeLink({
          click: (e, ctx) => {
            if (condition?.elmPrjId && ctx.item?.['TGT_EQP_ID']) {
              setOpenScheduleChart(true);
              setScheduleChartCondition({
                elmPrjId: condition?.elmPrjId,
                tgtEqpId: ctx.item?.['TGT_EQP_ID'],
                targetData: ctx.item,
              });
            }
          },
        }),
      },
      {
        header: String(t('setup.label.설비명', '설비명')),
        binding: 'EQP_NM',
        minWidth: 80,
        width: 140,
        align: 'left',
      },
      {
        header: String(t('setup.label.협력업체명', '협력업체명')),
        binding: 'EXCP_NM',
        minWidth: 80,
        width: 100,
        align: 'left',
      },
      {
        header: String(t('setup.label.타겟일정', '타겟일정')),
        binding: 'TGT_PLAN_END_DT',
        minWidth: 85,
        width: '*',
        align: 'center',
      },
      ...prjSpLayout,
    ];
  }, [code, isChangeColumn, condition]);

  const onInitialized = (grid) => {
    gridRef.current = grid;
    setIsChangeColumn(true);
    gridRef.current?.refresh();
  };

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('setup.label.일정현황', '일정현황'),
      t('setup.label.일정현황', '일정현황')
    );
  };

  const includePin = [
    'PRDN_PROC_CD_NM',
    'EQCL_ID',
    'ELTR_TP_CD_NM',
    'EQP_LN_ID',
    'EQP_ID',
    'EQP_NM',
    'EXCP_NM',
  ];

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.일정현황 목록', '일정현황 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(filterRowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <StatusDot style={{ marginRight: '10px' }}>
            <span className="done">{t('setup.label.시작전', '시작전')}</span>
            <span className="yellow">{t('setup.label.지연(30▼)', '지연(30▼)')}</span>
            <span className="red">{t('setup.label.지연(30▲)', '지연(30▲)')}</span>
            <span className="blue">{t('setup.label.정상', '정상')}</span>
            <span className="green">{t('setup.label.완료', '완료')}</span>
          </StatusDot>
          <div style={{ display: 'inline-flex', paddingRight: '10px', paddingTop: '5px' }}>
            <label htmlFor="delayYn" style={{ paddingTop: '0px' }}>
              {t('setup.label.지연여부', '지연여부')}
            </label>
            <Checkbox
              id="delayYn"
              css={st.checkbox}
              className="custom"
              checked={condition?.delayYn === 'Y'}
              onChange={(e, checked) => {
                setCondition((prev) => ({
                  ...prev,
                  delayYn: checked ? 'Y' : 'N',
                }));
              }}
              disableRipple
            />
          </div>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch(condition)}
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
          <GridSettingButtons
            gridId={'schdStatGrid'}
            layoutDefinition={layoutDefinition}
            changeShownColumns={(result) => {
              gridUiRef.current?.changeColumn?.(result);
              setIsChangeColumn(true);
            }}
          />
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={'schdStatGrid'}
        ref={gridUiRef}
        layoutDefinition={layoutDefinition}
        rowData={filterRowData}
        height={443}
        frozenColumns={6}
        isReadOnly={true}
        allowSorting={false}
        allowDragging={false}
        includeFilter={includePin}
        includePin={includePin}
        initialized={onInitialized}
        onChangeCheckedItem={(items) => setCheckedItems(items)}
      />
      <GlobalBtnGroup>
        {(ensolYn === 'N' || hasPrjMgrAuth) && (
          <Button
            css={IconButton.button}
            className="icoNone primaryBtn"
            onClick={async () => {
              if (await validate()) {
                onScheduleChange(condition?.elmPrjId || '', condition?.excpId || '', checkedItems);
              }
            }}
          >
            {t('setup.label.일정 수정', '일정 수정')}
          </Button>
        )}
      </GlobalBtnGroup>

      {isOpenScheduleChart && (
        <SetupEquipmentScheduleChartPopup
          open={isOpenScheduleChart}
          close={() => setOpenScheduleChart(false)}
          condition={{
            type: 'CMN',
            elmPrjId: scheduleChartCondition?.elmPrjId,
            tgtEqpId: scheduleChartCondition?.tgtEqpId,
            targetData: scheduleChartCondition?.targetData,
          }}
        />
      )}
    </>
  );
};

export default React.forwardRef(ScheduleStatusContent);
